import React, { useContext, useEffect, useState } from 'react';
import t from 'react-translate';
import { AngularServicesContext } from 'react-app';
import { BrowserRouter, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';

import { NvTab, NvResponsiveTabsDisplayType } from 'shared/components/nv-responsive-tabs';
import { NvResponsiveTabsRow } from 'shared/components/nv-responsive-tabs-row';

import { createGridStyles, quarterSpacing } from 'styles/global_defaults/scaffolding';
import { textSmallFontSize } from 'styles/global_defaults/fonts';
import { gray6, warning } from 'styles/global_defaults/colors';

import { AnalyticsDashBoardType } from 'redux/schemas/app/analytics';
import { getCurrentInstitution } from 'redux/selectors/institutions';

import NvIcon from 'shared/components/nv-icon';
import { getCurrentCourse } from 'redux/selectors/course';
import AnalyticsDashboard from './analytics-dashboard';
import PracticeAnalyticsTab from './practice-analytics-tab';
import QuizAndSurveyAnalyticsTab from './quiz-survey-analytics';
import QrveyWrapper from './qrvey-wrapper';
import QrveyPanelsWrapper from './qrvey-panels-wrapper';

const styles = css`
  .tab-container {
    position: sticky;
    top: 0;
    z-index: 2;
    border-bottom: solid 1px ${gray6};
  }
  .bottom-border {
    border-bottom: solid ${quarterSpacing}px ${warning}
  }
  .quiz-survey-dashboard {
    max-width: 1030px;
  }

  .practice-dropdown-title {
    align-items: center;
    display: flex;
    margin-left: 25px;

    .bs4-dropdown-menu {
      &.show {
        transform: translate3d(0%, 35px, 0px) !important;
        left: 20px !important;
      }
    }
  }
  .question-title {
    max-width: 800px;
  }
`;

enum TABS {
  OVERVIEW = 0,
  ACTIVITIES = 1,
  SKILLS = 2,
  QUIZ_SURVEY = 3,
  PRACTICE = 4,
}

const getCurrentActiveTab = (
  location,
  questionSetId,
  institutionHasSkilltags,
) => {
  let currentActiveTab = 0;

  const sections = [
    {
      url: '/activities',
      activeId: TABS.ACTIVITIES,
      display: true,
    },
    {
      url: '/skills',
      activeId: TABS.SKILLS,
      display: institutionHasSkilltags,
    },
  ];

  sections.forEach((section) => {
    if (location?.hash?.endsWith(section.url) && section.display && !questionSetId) {
      currentActiveTab = section.activeId;
    } else if (questionSetId) {
      currentActiveTab = TABS.QUIZ_SURVEY;
    }
  });

  return currentActiveTab;
};

const CourseAnalytics = () => {
  const { $state, CurrentPermissionsManager } = useContext(AngularServicesContext);

  const institution = useSelector(getCurrentInstitution);
  const currentCourse = useSelector(getCurrentCourse);
  const userCourse = useSelector((state) => state.models.enrollments[currentCourse.userCourse]);

  const institutionHasSkilltags = institution?.hasSkillTags;
  const history = useHistory();
  const location = useLocation();

  const [questionSetId, setQuestionSetId] = useState(null);
  const [activeTab, setActiveTab] = useState(getCurrentActiveTab(location, questionSetId, institutionHasSkilltags));
  const [prevTab, setPrevTab] = useState<number>(getCurrentActiveTab(location, questionSetId, institutionHasSkilltags));
  const [selectedPage, setSelectedPage] = useState<string>(null);

  const { catalogId } = $state?.params;
  const isInstructor = CurrentPermissionsManager.isInstructor();
  const isReportAdmin = CurrentPermissionsManager.isReportAdmin();
  const showCohorts = (currentCourse?.isPrimary || (currentCourse?.isCohort && userCourse?.numCohortsReportAdminOrInstructorIn >= 1)) && (isReportAdmin || isInstructor);

  const BASE_URL = `/#!/courses/${catalogId}/analytics`;
  const selectTab = (id) => {
    if (id === TABS.OVERVIEW) {
      window.location.href = BASE_URL;
    }
    if (id === TABS.ACTIVITIES) {
      window.location.href = `${BASE_URL}/activities`;
    }
    if (id === TABS.SKILLS && institutionHasSkilltags) {
      window.location.href = `${BASE_URL}/skills`;
    }

    setQuestionSetId(null);
  };

  const tabs: NvTab[] = [
    {
      text: t.COURSE_ANALYTICS.OVERVIEW(),
      onClick: () => selectTab(TABS.OVERVIEW),
    },
    {
      text: t.COURSE_ANALYTICS.ACTIVITIES(),
      onClick: () => selectTab(TABS.ACTIVITIES),
    },
  ];

  if (institutionHasSkilltags) {
    tabs.push({
      text: t.COURSE_ANALYTICS.SKILLS(),
      onClick: () => selectTab(TABS.SKILLS),
    });
  }

  const tabStyles = {
    ...createGridStyles(1, 2),
    fontSize: `${textSmallFontSize}px`,
  };

  const onSelectQuestion = (id: number) => {
    setQuestionSetId(id);
    setActiveTab(TABS.QUIZ_SURVEY);
    history.push(`${BASE_URL}/quiz-and-survey/${id}`);
  };

  // This useEffect manages the opening and closing of the Practice Room Modal.
  // When the user opens the Practice Room Modal, we create a new instance of QRvey.
  // Since QRvey does not support handling multiple instances simultaneously, we ensure
  // that any existing QRvey instance is closed before opening a new one.
  useEffect(() => {
    // Check if the Practice Room Modal is being opened
    if ($state?.current?.name === 'practice-room-modal') {
      // Update the selected page to 'practice-room-modal'
      setSelectedPage($state?.current?.name);

      // Store the current active tab before switching
      setPrevTab(activeTab);

      // Reset question set and activate the tab for the practice room
      setQuestionSetId(null);
      setActiveTab(TABS.PRACTICE); // Assuming 5 is the tab for the practice room
    } else if (selectedPage === 'practice-room-modal') {
      // If the Practice Room Modal is being closed, restore the previous state
      setPrevTab(TABS.OVERVIEW); // Reset the previous tab
      setSelectedPage(null);
      setActiveTab(prevTab); // Restore the active tab to the previous state
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [$state?.current?.name]); // Added dependencies for more accurate tracking

  useEffect(() => {
    if ($state.params?.questionId) {
      setQuestionSetId($state.params?.questionId);
      setActiveTab(TABS.QUIZ_SURVEY);
    }
  }, [$state.params?.questionId]);

  useEffect(() => {
    const currentActiveTab = getCurrentActiveTab(location, questionSetId, institutionHasSkilltags);
    if (currentActiveTab !== activeTab) {
      setActiveTab(currentActiveTab);
    }
  }, [location, institutionHasSkilltags, activeTab, questionSetId]);

  return (
    <div css={styles}>
      <div className='d-flex justify-content-between bg-white pr-4'>
        <div className='d-flex pr-2 bg-white tab-container'>
          <NvResponsiveTabsRow
            style={tabStyles}
            defaultTabs={tabs}
            tabType={NvResponsiveTabsDisplayType.TEXT_ONLY}
            tabTextClass='card-title'
            selectedTabIndex={activeTab}
            revertActiveTab={questionSetId ? -1 : activeTab}
          />
          <PracticeAnalyticsTab catalogId={catalogId} />
          <QuizAndSurveyAnalyticsTab
            onSelect={onSelectQuestion}
            selectedId={questionSetId}
          />
        </div>
        {showCohorts && (
          <div className='d-flex align-items-center'>
            <a
              href={$state?.href('cohort-analytics', { catalogId })}
              className='d-flex text-primary font-weight-bold'
              aria-labelledby='cohort-analytics-link'
            >
              <NvIcon icon='data' size='smallest' />
              <span className='ml-1' id='cohort-analytics-link'>
                {t.COURSE_ADMIN_MEGA_MENU.COHORTS_ANALYTICS.LABEL()}
              </span>
            </a>
          </div>
        )}
      </div>

      {/* This to conditionals are neccesary to rerender the qrvey instance on click of the tabs */}
      {activeTab === 0 && (
        <AnalyticsDashboard
          dashboardType={AnalyticsDashBoardType.COURSE}
          configKeyName='config_overview'
          pageIdKey='overview'
        >
          <QrveyWrapper />
        </AnalyticsDashboard>
      )}
      {activeTab === 1 && (
        <AnalyticsDashboard
          dashboardType={AnalyticsDashBoardType.COURSE}
          configKeyName='config_activities'
          pageIdKey='activities'
        >
          <QrveyWrapper />
        </AnalyticsDashboard>
      )}
      {activeTab === 2 && (
        <AnalyticsDashboard
          dashboardType={AnalyticsDashBoardType.COURSE}
          configKeyName='config_skills'
          pageIdKey='skills'
        >
          <QrveyWrapper />
        </AnalyticsDashboard>
      )}
      {questionSetId && (
        <div className='d-flex justify-content-center quiz-survey-dashboard mx-auto'>
          <AnalyticsDashboard
            dashboardType={AnalyticsDashBoardType.QUESTION_SET}
            configKeyName={`config_question_set_${questionSetId}`}
            pageIdKey='question_set'
            questionSetId={questionSetId}
          >
            <QrveyPanelsWrapper />
          </AnalyticsDashboard>
        </div>
      )}
    </div>
  );
};

export default CourseAnalytics;

export const CourseAnalyticsRoute = () => <BrowserRouter forceRefresh><CourseAnalytics /></BrowserRouter>;
